import React from 'react';
import { Users, ChevronDown, Clock, Activity } from 'lucide-react';

interface GroupInfo {
    id: number;
    title?: string;
    name?: string;
}

interface GroupData {
    group_id: number;
    group_name: string;
    member_count: number;
    last_updated: string;
}

interface GroupMonitorProps {
    groups: GroupInfo[];
    selectedGroup: number | null;
    onGroupChange: (groupId: number) => void;
    groupData: GroupData | null;
    loading: boolean;
}

export const GroupMonitor: React.FC<GroupMonitorProps> = ({
    groups,
    selectedGroup,
    onGroupChange,
    groupData,
    loading
}) => {
    return (
        <div style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: 'var(--glass-border)',
            borderRadius: 'var(--radius)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        }}>
            <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#fff',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
            }}>
                <div style={{ background: 'rgba(255, 106, 0, 0.1)', padding: '6px', borderRadius: '8px', display: 'flex' }}>
                    <Users size={18} color="#ff6a00" />
                </div>
                Monitoramento de Grupos
            </h3>

            {loading && !groupData ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
                    <div className="shimmer-loading" style={{ height: '48px', borderRadius: '10px', width: '100%' }}></div>
                    <div className="shimmer-loading" style={{ height: '160px', borderRadius: '12px', width: '100%', flex: 1 }}></div>
                </div>
            ) : groups.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ marginBottom: '24px', position: 'relative' }}>
                        <select
                            value={selectedGroup || ''}
                            onChange={(e) => onGroupChange(Number(e.target.value))}
                            style={{
                                width: '100%',
                                background: 'rgba(0, 0, 0, 0.4)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '10px',
                                padding: '14px 16px',
                                color: '#f4f4f5',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                appearance: 'none',
                                outline: 'none',
                                transition: 'border-color 0.2s ease',
                            }}
                            className="group-select-hover"
                        >
                            {groups.map(group => (
                                <option
                                    key={group.id}
                                    value={group.id}
                                    style={{
                                        background: '#0a0a0b',
                                        color: '#fff',
                                        padding: '12px'
                                    }}
                                >
                                    {group.title || group.name}
                                </option>
                            ))}
                        </select>
                        <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                            <ChevronDown size={18} color="#a1a1aa" />
                        </div>
                    </div>

                    {groupData ? (
                        <div style={{
                            background: 'radial-gradient(circle at top right, rgba(255, 106, 0, 0.08), transparent 60%), rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 106, 0, 0.15)',
                            borderRadius: '16px',
                            padding: '32px 24px',
                            textAlign: 'center',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <Activity size={120} color="rgba(255, 106, 0, 0.03)" style={{ position: 'absolute', left: '-20px', bottom: '-20px', pointerEvents: 'none' }} />

                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Membros Ativos
                            </div>

                            {loading ? (
                                <div className="shimmer-loading" style={{ height: '64px', width: '120px', borderRadius: '8px', marginBottom: '16px' }}></div>
                            ) : (
                                <div style={{
                                    fontSize: '64px',
                                    fontWeight: 700,
                                    color: '#fff',
                                    marginBottom: '16px',
                                    lineHeight: 1,
                                    textShadow: '0 0 30px rgba(255, 106, 0, 0.4)'
                                }}>
                                    {groupData.member_count.toLocaleString()}
                                </div>
                            )}

                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: 'rgba(0, 0, 0, 0.4)',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                border: '1px solid rgba(255, 255, 255, 0.05)'
                            }}>
                                <Clock size={12} color="#a1a1aa" />
                                <span style={{ fontSize: '12px', color: '#a1a1aa' }}>
                                    Hoje, {new Date(groupData.last_updated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div className="shimmer-loading" style={{ height: '100%', width: '100%', borderRadius: '16px' }}></div>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    padding: '32px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: '16px',
                    border: '1px dashed rgba(255,255,255,0.1)'
                }}>
                    <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px'
                    }}>
                        <Users size={28} color="#71717a" />
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px', color: '#e4e4e7' }}>Nenhum grupo configurado</div>
                    <div style={{ fontSize: '13px', color: '#a1a1aa', maxWidth: '240px', lineHeight: 1.5 }}>
                        Conecte seu sistema a grupos do Telegram para monitorar membros.
                    </div>
                </div>
            )}
        </div>
    );
};
